import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OPS } from "@/constants/testIds";
import { DK_REASON_MIN, DK_TIMEOUT_MS, allowsDecimalOf, dkError, dkMessage, fmtQuantity, unitOf } from "./dkUtils";
import { cleanQty, num } from "./dayModel";
import { QtyField } from "./widgets";

const D = OPS.dk.overrideDialog;

/**
 * «تعديل الرصيد الافتتاحي»: a new opening for one item on one day, with a
 * required reason (≥ 5 characters, the server's rule; audited as
 * `dk_opening_override`). When the opening is already an override, «إلغاء
 * التعديل» clears it (`opening: null`) and the chain value comes back. The
 * reason is cleared every time the dialog opens, and the dialog cannot be
 * dismissed while the request runs. `onDone()` reloads the day.
 */
export function OpeningDialog({ open, onOpenChange, locationId, date, item, line, onDone }) {
    const { t, tn, lang } = useI18n();
    const [value, setValue] = useState("");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(null);   // "set" | "clear" | null
    const [error, setError] = useState(null);
    const isOverride = line?.opening_source === "override";

    useEffect(() => {
        if (!open) return;
        setValue(line?.opening === null || line?.opening === undefined ? "" : String(line.opening));
        setReason("");
        setError(null);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const reasonOk = reason.trim().length >= DK_REASON_MIN;
    const valueOk = num(value) !== null;

    const send = async (opening, mode) => {
        setBusy(mode);
        setError(null);
        try {
            await api.post("/dk/day/opening",
                           { location_id: locationId, date, item_id: item.id, opening, reason: reason.trim() },
                           { timeout: DK_TIMEOUT_MS });
            toast.success(t(mode === "clear" ? "dk_override_cleared" : "dk_override_saved"));
            onOpenChange(false);
            onDone && onDone();
        } catch (e) {
            const d = dkError(e);
            if (d) setError(lang === "ar" ? d.message_ar || d.message_en : d.message_en || d.message_ar);
            toast.error(dkMessage(e, lang) || t("dk_offline_error"));
        } finally {
            setBusy(null);
        }
    };

    if (!item) return null;
    const unit = unitOf(item, t);
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
            <DialogContent data-testid={D.dialog} className="max-h-[88vh] overflow-y-auto bg-[color:var(--bg-surface)] sm:max-w-md">
                <DialogHeader className="text-start sm:text-start">
                    <DialogTitle>{t("dk_override_title")} — {tn(item)}</DialogTitle>
                    <DialogDescription>{t("dk_override_hint")}</DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    {line ? (
                        <p className="text-xs text-[color:var(--ink-muted)]">
                            {t("dk_override_current")}:{" "}
                            <span className="font-bold text-[color:var(--ink)]">
                                {line.opening === null || line.opening === undefined
                                    ? t("dk_opening_none")
                                    : <><bdi dir="ltr">{fmtQuantity(line.opening, lang)}</bdi> {unit}</>}
                            </span>
                        </p>
                    ) : null}
                    <label className="block space-y-1.5 text-sm font-semibold text-[color:var(--ink)]">
                        <span>{t("dk_override_value")}</span>
                        <QtyField testId={D.value} value={value} onChange={setValue} unit={unit}
                                  allowsDecimal={allowsDecimalOf(item)}
                                  label={t("dk_override_value")} disabled={!!busy} />
                    </label>
                    <label className="block space-y-1.5 text-sm font-semibold text-[color:var(--ink)]">
                        <span>{t("dk_override_reason")}</span>
                        <Textarea data-testid={D.reason} value={reason} maxLength={500} rows={3} disabled={!!busy}
                                  onChange={(e) => setReason(e.target.value)} className="text-base" />
                        <span className="block text-[11px] font-normal text-[color:var(--ink-faint)]">{t("dk_override_reason_min")}</span>
                    </label>
                    {error ? <p className="text-xs font-semibold text-[color:var(--danger)]">{error}</p> : null}
                </div>

                <DialogFooter className="gap-2 sm:gap-2">
                    <Button type="button" variant="outline" data-testid={D.cancel} disabled={!!busy} onClick={() => onOpenChange(false)}>
                        {t("dk_cancel")}
                    </Button>
                    {isOverride ? (
                        <Button type="button" variant="outline" data-testid={D.clear} disabled={!!busy || !reasonOk}
                                onClick={() => send(null, "clear")}>
                            {busy === "clear" ? t("dk_saving") : t("dk_override_clear")}
                        </Button>
                    ) : null}
                    <Button type="button" data-testid={D.confirm} disabled={!!busy || !reasonOk || !valueOk}
                            onClick={() => send(cleanQty(value), "set")}>
                        {busy === "set" ? t("dk_saving") : t("dk_override_confirm")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
